const HONORIFICS_AND_SUFFIXES = new Set([
	"mr",
	"mrs",
	"ms",
	"miss",
	"dr",
	"prof",
	"jr",
	"sr",
	"ii",
	"iii",
	"iv",
]);

const MAX_EDIT_DISTANCE_BY_LENGTH: Record<number, number> = {
	2: 0,
	3: 1,
	4: 1,
	5: 1,
};

const DEFAULT_MAX_EDIT_DISTANCE = 2;

function normalizeName(name: string): string {
	return name
		.normalize("NFD")
		.replace(/\p{M}/gu, "")
		.toLowerCase()
		.replace(/[''`]/g, "")
		.replace(/[.-]/g, " ")
		.replace(/[^a-z0-9\s]/g, " ")
		.replace(/\s+/g, " ")
		.trim();
}

function tokenizeName(name: string): string[] {
	return normalizeName(name)
		.split(" ")
		.filter((token) => token.length > 0 && !HONORIFICS_AND_SUFFIXES.has(token));
}

function levenshteinDistance(a: string, b: string): number {
	if (a === b) {
		return 0;
	}

	if (a.length === 0) {
		return b.length;
	}

	if (b.length === 0) {
		return a.length;
	}

	const rows = a.length + 1;
	const cols = b.length + 1;
	const matrix: number[][] = Array.from({ length: rows }, () =>
		Array.from({ length: cols }, () => 0),
	);

	for (let i = 0; i < rows; i++) {
		matrix[i][0] = i;
	}

	for (let j = 0; j < cols; j++) {
		matrix[0][j] = j;
	}

	for (let i = 1; i < rows; i++) {
		for (let j = 1; j < cols; j++) {
			const substitutionCost = a[i - 1] === b[j - 1] ? 0 : 1;
			matrix[i][j] = Math.min(
				matrix[i - 1][j] + 1,
				matrix[i][j - 1] + 1,
				matrix[i - 1][j - 1] + substitutionCost,
			);
		}
	}

	return matrix[a.length][b.length];
}

function maxAllowedEditDistance(tokenLength: number): number {
	if (tokenLength <= 2) {
		return MAX_EDIT_DISTANCE_BY_LENGTH[2];
	}

	if (tokenLength <= 5) {
		return MAX_EDIT_DISTANCE_BY_LENGTH[tokenLength] ?? 1;
	}

	return DEFAULT_MAX_EDIT_DISTANCE;
}

function tokensFuzzyMatch(
	requiredToken: string,
	candidateToken: string,
): boolean {
	if (requiredToken === candidateToken) {
		return true;
	}

	if (requiredToken.length === 1 && candidateToken.startsWith(requiredToken)) {
		return true;
	}

	if (candidateToken.length === 1 && requiredToken.startsWith(candidateToken)) {
		return true;
	}

	const distance = levenshteinDistance(requiredToken, candidateToken);
	return distance <= maxAllowedEditDistance(requiredToken.length);
}

function allRequiredTokensMatch(
	requiredTokens: string[],
	cardholderTokens: string[],
): boolean {
	const unusedCardholderTokens = [...cardholderTokens];

	for (const requiredToken of requiredTokens) {
		const matchIndex = unusedCardholderTokens.findIndex((cardToken) =>
			tokensFuzzyMatch(requiredToken, cardToken),
		);

		if (matchIndex === -1) {
			return false;
		}

		unusedCardholderTokens.splice(matchIndex, 1);
	}

	return true;
}

function doesAnyTokenMatch(
	sourceTokens: string[],
	candidateTokens: string[],
): boolean {
	return sourceTokens.some((sourceToken) =>
		candidateTokens.some((candidateToken) =>
			tokensFuzzyMatch(sourceToken, candidateToken),
		),
	);
}

function doesPersonalNameMatchCardholder(
	guestFirstName: string,
	guestLastName: string,
	cardholderName: string,
): boolean {
	const firstNameTokens = tokenizeName(guestFirstName);
	const lastNameTokens = tokenizeName(guestLastName);

	if (firstNameTokens.length === 0 || lastNameTokens.length === 0) {
		return false;
	}

	const cardholderTokens = tokenizeName(cardholderName);

	if (cardholderTokens.length === 0) {
		return false;
	}

	return (
		doesAnyTokenMatch(firstNameTokens, cardholderTokens) &&
		doesAnyTokenMatch(lastNameTokens, cardholderTokens)
	);
}

function doesBusinessNameMatchCardholder(
	businessName: string,
	cardholderName: string,
): boolean {
	const businessTokens = tokenizeName(businessName);
	const cardholderTokens = tokenizeName(cardholderName);

	if (businessTokens.length === 0 || cardholderTokens.length === 0) {
		return false;
	}

	return (
		allRequiredTokensMatch(businessTokens, cardholderTokens) ||
		allRequiredTokensMatch(cardholderTokens, businessTokens)
	);
}

export function doesGuestNameMatchCardholder(
	guestFirstName: string,
	guestLastName: string,
	cardholderName: string,
	businessName?: string | null,
): boolean {
	if (
		doesPersonalNameMatchCardholder(
			guestFirstName,
			guestLastName,
			cardholderName,
		)
	) {
		return true;
	}

	const trimmedBusinessName = businessName?.trim();
	if (!trimmedBusinessName) {
		return false;
	}

	return doesBusinessNameMatchCardholder(trimmedBusinessName, cardholderName);
}
